import { parseJSON } from 'date-fns';
import { useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Card,
    Chip,
    Stack,
    Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';

import { Order } from '@barboxx/models';

import { BaseTextInput } from './BaseTextInput';
import { FaceVerificationDiagnosticsSection } from './FaceVerificationDiagnosticsSection';
import { FaceVerificationImagesSection } from './FaceVerificationImagesSection';
import { FaceVerificationOverrideSection } from './FaceVerificationOverrideSection';

const FACE_VERIFICATION_STATUS_COLOR: Record<
    string,
    'default' | 'success' | 'error' | 'warning'
> = {
    Passed: 'success',
    Failed: 'error',
    Overridden: 'warning',
    Pending: 'default',
};

export function FaceVerificationCard({
    order,
    imageUrls,
    imageUrlsLoading = false,
    faceVerificationExpanded,
    onFaceVerificationExpandedChange,
}: {
    order: Order.DetailsResponse;
    imageUrls?: Order.ImageUrlsResponse;
    imageUrlsLoading?: boolean;
    faceVerificationExpanded: boolean;
    onFaceVerificationExpandedChange: (expanded: boolean) => void;
}) {
    const faceStatus = order.pickupFaceVerificationStatus;
    const faceStatusColor = faceStatus
        ? (FACE_VERIFICATION_STATUS_COLOR[faceStatus] ?? 'default')
        : 'default';
    const [showDebugOverlays, setShowDebugOverlays] = useState(false);

    if (!faceStatus) {
        return null;
    }

    return (
        <Card>
            <Accordion
                expanded={faceVerificationExpanded}
                onChange={(_event, expanded) =>
                    onFaceVerificationExpandedChange(expanded)
                }
                TransitionProps={{ unmountOnExit: true }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                        px: 3,
                        py: 1,
                        minHeight: 72,
                        '& .MuiAccordionSummary-content': {
                            my: 0,
                            alignItems: 'center',
                        },
                        '&.Mui-expanded': {
                            minHeight: 72,
                        },
                        '&.Mui-expanded .MuiAccordionSummary-content': {
                            my: 0,
                        },
                    }}
                >
                    <Stack
                        direction="row"
                        gap={1}
                        flexWrap="wrap"
                        alignItems="center"
                    >
                        <Typography variant="h6">Face Verification</Typography>
                        <Chip
                            label={faceStatus}
                            color={faceStatusColor}
                            size="small"
                        />
                        {order.pickupFaceVerificationScore != null ? (
                            <Chip
                                size="small"
                                variant="outlined"
                                label={`Score: ${order.pickupFaceVerificationScore}%`}
                            />
                        ) : null}
                        {order.pickupFaceVerificationDiagnostics?.summary
                            .attemptCount != null ? (
                            <Chip
                                size="small"
                                variant="outlined"
                                label={`Attempt: ${order.pickupFaceVerificationDiagnostics.summary.attemptCount}`}
                            />
                        ) : null}
                    </Stack>
                </AccordionSummary>
                <AccordionDetails>
                    <Stack gap={2}>
                        <Stack direction="row" gap={2} alignItems="center">
                            <Typography variant="body2" color="text.secondary">
                                Status
                            </Typography>
                            <Chip
                                label={faceStatus}
                                color={faceStatusColor}
                                size="small"
                            />
                        </Stack>

                        <Stack direction="row" gap={2} flexWrap="wrap">
                            {order.pickupFaceVerificationScore != null && (
                                <BaseTextInput
                                    label="Match Score"
                                    value={`${order.pickupFaceVerificationScore}%`}
                                    sx={{ minWidth: 140 }}
                                />
                            )}
                            {order.pickupFaceVerifiedAt && (
                                <DateTimePicker
                                    label="Verified At"
                                    value={parseJSON(
                                        order.pickupFaceVerifiedAt
                                    )}
                                    format="MM/dd/yyyy hh:mm:ss aa"
                                    readOnly
                                    sx={{ minWidth: 240 }}
                                />
                            )}
                        </Stack>

                        {order.pickupFaceVerificationDiagnostics && (
                            <FaceVerificationDiagnosticsSection
                                diagnostics={
                                    order.pickupFaceVerificationDiagnostics
                                }
                            />
                        )}

                        {order.pickupFaceVerificationOverride && (
                            <FaceVerificationOverrideSection
                                override={order.pickupFaceVerificationOverride}
                            />
                        )}

                        <FaceVerificationImagesSection
                            imageUrls={imageUrls}
                            imageUrlsLoading={imageUrlsLoading}
                            diagnostics={
                                order.pickupFaceVerificationDiagnostics
                            }
                            showDebugOverlays={showDebugOverlays}
                            onShowDebugOverlaysChange={setShowDebugOverlays}
                        />
                    </Stack>
                </AccordionDetails>
            </Accordion>
        </Card>
    );
}
